import { memo, useEffect, useRef } from 'react';
import { Typography } from '@mui/material';
import { focusNextInputField } from '../Options';
import AdditionalInfo from './AdditionalInfo';
import AlertInfo from './AlertInfo';
import InventoryOption from './InventoryOption';
import EquipmentOption from './EquipmentOption/EquipmentOption';
import HighlightText from 'components/UI/Inputs/AIQAutocomplete/HighlightText';
import { ALERT_NAME_DEFAULT } from 'utils/lookupHelper';
import PropTypes from 'prop-types';
import styles from './Option.module.css';

function Option({
    option,
    onChange,
    setDropdownOpened,
    setInputValue,
    activeOption,
    selectedOption,
    setSelectedOptionIndex,
    optionIndex,
    getInputValue,
    hasAlerts,
    referenceExpr,
    setReferenceValue,
    onSelect,
    isInventoryProperty,
    fieldName,
    isClientEquipmentEnabled,
    inputValue,
}) {
    const optionLabel = getInputValue(option);
    const optionRef = useRef();

    useEffect(() => {
        if (activeOption && inputValue === optionLabel) {
            setSelectedOptionIndex(optionIndex);
        }
    }, [activeOption, optionIndex, setSelectedOptionIndex, inputValue, optionLabel]);

    useEffect(() => {
        if (selectedOption && optionRef.current) {
            const container = optionRef.current.closest('[class*="option-container"]') || optionRef.current;
            container.scrollIntoView({
                block: 'nearest',
                behavior: 'auto',
            });
        }
    }, [selectedOption]);

    const baseStyles = `${styles['option']}`;
    const showActiveStyle = activeOption && inputValue === optionLabel;
    const stateStyles = showActiveStyle ? styles['option--active'] : selectedOption ? styles['option--selected'] : '';

    const optionStyles = `${styles['option-container']} ${baseStyles} ${stateStyles}`;
    const optionStylesWithAlerts = `${baseStyles} ${styles['option-container--alerts']} ${stateStyles}`;

    const mouseDownHandler = (e) => {
        setInputValue(getInputValue(option));
        onSelect?.(option);
        onChange(e, option);
        setDropdownOpened(false);
        referenceExpr && setReferenceValue(option?.[referenceExpr]);
        const autocompleteContainer = optionRef.current?.closest('div[class*="AIQAutocomplete"]');
        const inputEl = autocompleteContainer?.querySelector('input');
        if (!inputEl) return;
        focusNextInputField(+inputEl.id);
    };

    // The "Default" group is the absence of an authorization, not an alert: the
    // selector attaches one to every person/company option, so rendering it cost
    // an MUI Stack + a Note (with its own HighlightText blocks and a "Period
    // Access" line) on the majority of rows, showing only bulk-upload
    // provenance. Filtered here at render time only — `option.alerts` itself is
    // left intact, because getFieldColorFromAlerts branches on its length.
    const realAlerts = option.alerts?.filter((alert) => alert?.name !== ALERT_NAME_DEFAULT);

    if (hasAlerts) {
        return (
            <div className={optionStylesWithAlerts} onMouseDown={mouseDownHandler}>
                <Typography className={styles['option-name']} fontWeight={600} ref={optionRef}>
                    <HighlightText text={optionLabel} searchTerm={inputValue} />
                </Typography>
                <AdditionalInfo option={option} hasAlerts={hasAlerts} searchTerm={inputValue} />
                {!!realAlerts?.length && <AlertInfo alerts={realAlerts} searchTerm={inputValue} />}
            </div>
        );
    }

    if (isInventoryProperty && (option?.inventoryItemType || isClientEquipmentEnabled)) {
        return (
            <div className={optionStyles} onMouseDown={mouseDownHandler}>
                {isClientEquipmentEnabled ? (
                    <EquipmentOption option={option} fieldName={fieldName} optionRef={optionRef} searchTerm={inputValue} />
                ) : (
                    <InventoryOption option={option} fieldName={fieldName} optionRef={optionRef} searchTerm={inputValue} />
                )}
            </div>
        );
    }

    return (
        <div className={optionStyles} onMouseDown={mouseDownHandler}>
            <p className={styles[`option-text`]} ref={optionRef}>
                <HighlightText text={optionLabel} searchTerm={inputValue} />
            </p>
        </div>
    );
}

Option.propTypes = {
    option: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    setDropdownOpened: PropTypes.func,
    setInputValue: PropTypes.func,
    activeOption: PropTypes.bool,
    selectedOption: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    setSelectedOptionIndex: PropTypes.func,
    optionIndex: PropTypes.number,
    getInputValue: PropTypes.func,
    hasAlerts: PropTypes.bool,
    referenceExpr: PropTypes.string,
    setReferenceValue: PropTypes.func,
    onSelect: PropTypes.func,
    isInventoryProperty: PropTypes.bool,
    fieldName: PropTypes.string,
    isClientEquipmentEnabled: PropTypes.bool,
    inputValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// Shallow compare on every prop. All the handler props Options passes are
// referentially stable while the user is typing (`onChange` and
// `setSelectedOptionIndex` are useCallback'd in useDropdownBehavior; the rest
// are setState functions or primitives), so a default shallow memo is both
// correct and effective here — and unlike a hand-written comparator it cannot
// go stale if a new prop is added later.
export default memo(Option);
